/**
 * Tiny promise-based IndexedDB wrapper for the offline feature.
 *
 * Stores:
 *  - outbox : queued mutations made while offline (sales, ...) awaiting sync.
 *  - kv     : key/value cache (product catalog, customers, etc.) for offline use.
 */
const DB_NAME = 'nexinv-offline';
const DB_VERSION = 1;

let dbPromise = null;

function open() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) { reject(new Error('IndexedDB unavailable')); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('outbox')) {
        db.createObjectStore('outbox', { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains('kv')) {
        db.createObjectStore('kv', { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(store, mode, fn) {
  return open().then(db => new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const s = t.objectStore(store);
    let result;
    Promise.resolve(fn(s)).then(r => { result = r; });
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  }));
}

function reqP(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export const outbox = {
  add(record) {
    return tx('outbox', 'readwrite', s => reqP(s.add({ ...record, createdAt: Date.now(), status: 'pending' })));
  },
  all() {
    return tx('outbox', 'readonly', s => reqP(s.getAll())).then(r => r || []);
  },
  update(id, patch) {
    return tx('outbox', 'readwrite', s => reqP(s.get(id)).then(rec => {
      if (rec) s.put({ ...rec, ...patch });
    }));
  },
  remove(id) {
    return tx('outbox', 'readwrite', s => reqP(s.delete(id)));
  },
  count() {
    return tx('outbox', 'readonly', s => reqP(s.count())).then(n => n || 0);
  },
};

export const kv = {
  get(key) {
    return tx('kv', 'readonly', s => reqP(s.get(key))).then(r => (r ? r.value : null));
  },
  set(key, value) {
    return tx('kv', 'readwrite', s => reqP(s.put({ key, value })));
  },
};
